import { J2Add } from '@e3dc-react/icons'
import { ButtonWithLoading } from '@e3dc-react/shell/Elements/Buttons'
import { Paper, PaperContent } from '@e3dc-react/shell/Elements/Paper'
import { ApiTableSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { Table } from '@e3dc-react/shell/Elements/Table'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, SxProps, Theme } from '@mui/material'
import { Formik, FormikHelpers } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { useInstallationAccessTable, useInstallationInvitation, useOwnerAccessRequest } from 'src/Libs/installation-rights-management/hooks'
import useInstallationAccessForm from 'src/Libs/installation-rights-management/hooks/use-installation-access-form'
import { InstallationInvitationTableFormModel } from 'src/Libs/installation-rights-management/models'
import { Installation } from 'src/Libs/installations/models/installation.model'
import { RIGHTS_ADMINISTRATION_PERMISSIONS_ID } from 'src/Libs/qa/ids'
import { useTypedSelector } from 'src/State/RootReducer'
import { AccountLinksContext } from '../provider/AccountLinksContext'
import { canAssignNewOwner } from '../utils/owner-handover'
import BlankView from 'src/Components/blank-view/components/BlankView'

interface Props {
  installation: Installation
  cloudKeys: React.ReactNode[][]
}

const UserAccessAndInvitationsView: React.FC<Props> = ({ installation, cloudKeys }) => {
  const { t } = useCustomTranslation()
  const { inviteSomeoneDialog } = useInstallationInvitation()
  const { header, data, refreshData, actionMenus, accountLinks } = useInstallationAccessTable(installation)
  const { submitBulkUpdate, formData } = useInstallationAccessForm(accountLinks)
  const { updateAccountLinks, isLoadingAccountLinks } = useContext(AccountLinksContext)
  const { accessRequests } = useOwnerAccessRequest(installation.id)
  const user = useTypedSelector(state => state.user.userData)
  const [hasLoaded, setHasLoaded] = useState(false)

  useEffect(() => {
    if (installation && !isLoadingAccountLinks) {
      refreshData()
        .then(() => setHasLoaded(true))
        .catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountLinks, isLoadingAccountLinks])

  /* updating the cloud keys can have side effects on account links (e.g. deleting cloud key should delete account link). */
  /* updating access request have side effect on account links permission property */
  useEffect(() => {
    updateAccountLinks?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloudKeys, accessRequests])

  const handleInvite = (isInvitingOwner?: boolean) => (): void => {
    if (installation) inviteSomeoneDialog(Number(installation.id), isInvitingOwner).finally(refreshData)
  }

  const handleSubmit = (values: InstallationInvitationTableFormModel, actions: FormikHelpers<InstallationInvitationTableFormModel>): void => {
    submitBulkUpdate(values)
      .then(() => {
        refreshData()
        updateAccountLinks?.()
      })
      .catch(console.error)
      .finally(() => {
        actions.setSubmitting(false)
        actions.resetForm({ values })
      })
  }

  const showInviteOwnerButton = canAssignNewOwner(installation, user)

  if (hasLoaded && formData.items.length === accountLinks.length) {
    return (
      <Formik initialValues={formData} onSubmit={handleSubmit} enableReinitialize>
        {({ handleSubmit, dirty, isValid, isSubmitting }) => {
          return (
            <Paper title={t('installation.access-and-invitations.title')} dataQa={RIGHTS_ADMINISTRATION_PERMISSIONS_ID}>
              <PaperContent>
                <PaperContentTable
                  data={data}
                  header={header}
                  actionMenus={actionMenus}
                  showInviteOwnerButton={showInviteOwnerButton}
                  isSubmitting={isSubmitting}
                  dirty={dirty}
                  isValid={isValid}
                  onInvite={handleInvite}
                  onSave={handleSubmit}
                />
              </PaperContent>
            </Paper>
          )
        }}
      </Formik>
    )
  }

  return (
    <Paper title={t('installation.access-and-invitations.title')}>
      <ApiTableSkeleton withFilter={false} rowAmount={2} />
    </Paper>
  )
}

export default UserAccessAndInvitationsView

const controlsContainerSx: SxProps<Theme> = theme => ({
  mt: 5,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 2,

  '& div': {
    width: '100%',
  },

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',

    '&& div': {
      width: 'fit-content',
    },
  },
})

const buttonsGroupSx: SxProps<Theme> = theme => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,

  [theme.breakpoints.up('sm')]: {
    flexDirection: 'row',
  },
})

const saveButtonSx: SxProps<Theme> = theme => ({
  width: '100%',

  [theme.breakpoints.up('sm')]: { width: 'max-content' },
})

// #region PaperContentTable
interface PaperContentTableProps {
  data: React.ReactNode[][]
  header: React.ComponentProps<typeof Table>['header']
  actionMenus: React.ReactNode[]
  showInviteOwnerButton: boolean
  isSubmitting: boolean
  dirty: boolean
  isValid: boolean
  onInvite: (isInvitingOwner?: boolean) => () => void
  onSave: () => void
}

const PaperContentTable: React.FC<PaperContentTableProps> = ({
  data,
  header,
  actionMenus,
  showInviteOwnerButton,
  isSubmitting,
  dirty,
  isValid,
  onInvite,
  onSave,
}) => {
  const { t } = useCustomTranslation()

  const renderTable = (): React.ReactNode => {
    if (data.length === 0) {
      return (
        <BlankView
          title={t('installation.access-and-invitations.no-data-title')}
          explanation={t('installation.access-and-invitations.no-data-subtitle')}
          imageVariant={'NEUTRAL'}
        />
      )
    }

    return (
      <Table
        data={data}
        hasColoredTableColumns
        header={header}
        bodyElevation={0}
        footerElevation={0}
        headerElevation={0}
        customFunctionalities={[actionMenus]}
      />
    )
  }

  return (
    <>
      {renderTable()}
      <Box sx={controlsContainerSx}>
        <Box sx={buttonsGroupSx}>
          <Button onClick={onInvite()} variant={'outlined'} startIcon={<J2Add />}>
            {t('installation.access-and-invitations.controls.invite')}
          </Button>
          {showInviteOwnerButton && (
            <Button onClick={onInvite(true)} variant={'outlined'} startIcon={<J2Add />}>
              {t('installation.access-and-invitations.controls.invite-owner')}
            </Button>
          )}
        </Box>
        {data.length > 0 && (
          <ButtonWithLoading sx={saveButtonSx} isLoading={isSubmitting} onClick={onSave} disabled={!dirty || !isValid} label={t('common.save')} />
        )}
      </Box>
    </>
  )
}
// #endregion
